import { Card, ConfigProvider, Layout } from '@arco-design/web-react';
import { useEditorProps, useFocusIdx } from '@bay-email/editor';
import React, { useEffect } from 'react';
import { InteractivePrompt } from '../InteractivePrompt';
import styles from './index.module.scss';
import enUS from '@arco-design/web-react/es/locale/en-US';
import { MergeTagBadgePrompt } from '@extensions/MergeTagBadgePrompt';
import { MergeTagInlinePopover } from '@extensions/MergeTagInlinePopover';
import { EditPanel } from '../EditPanel';
import { ListingPickerModal } from '../EditPanel/Blocks/ListingPickerModal';
import { ConfigurationPanel } from '@extensions/ConfigurationPanel';
import { BlockAttributeConfigurationManager } from '@extensions/AttributePanel';
import {
	ExtensionProps,
	ExtensionProvider,
} from '@extensions/components/Providers/ExtensionProvider';
import {
	AdvancedType,
	DynamicType,
	DynamicImageBlock,
	BlockManager,
	t,
} from '@bay-email/core';
import { DynamicImage as DynamicImagePanel } from '@extensions/AttributePanel/components/blocks/DynamicImage';

let isDynamicRegistered = false;

function ensureDynamicBlocksRegistered() {
	if (isDynamicRegistered) return;

	BlockManager.registerBlocks({
		[DynamicType.IMAGE]: DynamicImageBlock,
	});

	BlockAttributeConfigurationManager.add({
		[DynamicType.IMAGE]: DynamicImagePanel,
	});

	isDynamicRegistered = true;
}

const defaultCategories: ExtensionProps['categories'] = [
	{
		get label() {
			return t('Content');
		},
		active: true,
		blocks: [
			{
				type: AdvancedType.TEXT,
			},
			{
				type: AdvancedType.IMAGE,
				payload: { attributes: { padding: '0px 0px 0px 0px' } },
			},
			{
				type: AdvancedType.BUTTON,
			},
			{
				type: AdvancedType.SOCIAL,
			},
			{
				type: AdvancedType.DIVIDER,
			},
			{
				type: AdvancedType.SPACER,
			},
			{
				type: AdvancedType.HERO,
			},
			{
				type: AdvancedType.WRAPPER,
			},
		],
	},
	{
		get label() {
			return t('Sections');
		},
		active: true,
		displayType: 'column',
		blocks: [
			{
				get title() {
					return t('1 column');
				},
				payload: [['100%']],
			},
			{
				get title() {
					return t('2 columns');
				},
				payload: [
					['50%', '50%'],
					['33%', '67%'],
					['67%', '33%'],
					['25%', '75%'],
					['75%', '25%'],
				],
			},
			{
				get title() {
					return t('3 columns');
				},
				payload: [
					['33.33%', '33.33%', '33.33%'],
					['25%', '25%', '50%'],
					['50%', '25%', '25%'],
				],
			},
			{
				get title() {
					return t('4 columns');
				},
				payload: [['25%', '25%', '25%', '25%']],
			},
		],
	},
];

export const StandardLayout: React.FC<ExtensionProps> = props => {
	ensureDynamicBlocksRegistered();

	const { height: containerHeight } = useEditorProps();
	const {
		showSourceCode = true,
		compact = true,
		categories = defaultCategories,
		jsonReadOnly = false,
		mjmlReadOnly = true,
	} = props;

	const { setFocusIdx } = useFocusIdx();

	useEffect(() => {
		if (!compact) {
			setFocusIdx('');
		}
	}, [compact, setFocusIdx]);

	return (
		<ExtensionProvider
			{...props}
			categories={categories}
		>
			<ConfigProvider locale={enUS}>
				<Card
					bordered={false}
					style={{ padding: 0, height: containerHeight, overflow: 'hidden' }}
					bodyStyle={{
						padding: 0,
						height: '100%',
						overflow: 'hidden',
					}}
				>
					<Layout
						className={styles.StandardLayout}
						style={{
							display: 'flex',
							width: '100%',
							height: '100%',
						}}
					>
						{compact && (
							<EditPanel
								showSourceCode={showSourceCode}
								jsonReadOnly={jsonReadOnly}
								mjmlReadOnly={mjmlReadOnly}
							/>
						)}
						<Layout
							style={{
								height: '100%',
								flex: 1,
								display: 'flex',
								flexDirection: 'column',
							}}
						>
							{props.children}
						</Layout>
						{!compact && (
							<EditPanel
								showSourceCode={showSourceCode}
								jsonReadOnly={jsonReadOnly}
								mjmlReadOnly={mjmlReadOnly}
							/>
						)}
						{compact ? (
							<Layout.Sider
								className={styles.configPanel}
								style={{
									height: '100%',
									minWidth: 300,
									maxWidth: 360,
									width: 360,
								}}
							>
								<ConfigurationPanel
									compact={compact}
									height={'100%'}
									showSourceCode={showSourceCode}
									jsonReadOnly={jsonReadOnly}
									mjmlReadOnly={mjmlReadOnly}
								/>
							</Layout.Sider>
						) : (
							<Layout.Sider style={{ width: 0, overflow: 'hidden' }} />
						)}
					</Layout>
				</Card>
				<InteractivePrompt />
				<MergeTagBadgePrompt />
				<MergeTagInlinePopover />
				<ListingPickerModal />
			</ConfigProvider>
		</ExtensionProvider>
	);
};
